import { captureBillingException } from '../../utils/report-error';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { updateBillingContact, getPartialBillingData } from '../../data/api';
import SpinnerButton from '../shared/SpinnerButton';
export default function BillingContact({ user, onCloseModal, setModalLoadingState }) {
    const buttonStates = {
        success: {
            className: 'gh-btn gh-btn-block',
            html: '&#10004; Update complete!',
            type: 'submit',
            disabled: true,
        },
        fail: {
            className: 'gh-btn gh-btn-block',
            html: 'Try again',
            type: 'submit',
            disabled: false,
        },
        pending: {
            className: 'gh-btn gh-btn-block spinner',
            html: '<span><div class="gh-spinner"></div></span>',
            disabled: true,
        },
        default: {
            className: 'gh-btn gh-btn-block',
            html: 'Update',
            type: 'submit',
            disabled: false,
        },
    };

    const [billingContact, setBillingContact] = useState(user?.billing_contact || '');
    const [error, setError] = useState(null);
    const [buttonState, setButtonState] = useState({ ...buttonStates.default });

    const isValidEmail = (email) => {
        return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email);
    };

    const sanitizeInput = (event) => {
        event.preventDefault();

        const cleanBillingContact = billingContact?.length ? billingContact.trim() : '';

        if (cleanBillingContact && !isValidEmail(cleanBillingContact)) {
            setError('Enter a valid email address');
            return;
        }

        setBillingContact(cleanBillingContact);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (billingContact && !isValidEmail(billingContact)) {
            setError('Enter a valid email address');
            return;
        }

        if (!user || !user?.id) {
            setError('Unable to update billing contact. Refresh the page and try again.');
            return;
        }

        setModalLoadingState(true);
        setButtonState({ ...buttonStates.pending });
        setError(null);

        const res = await updateBillingContact({
            userId: user.id,
            billingContact: billingContact?.length ? billingContact.trim() : '',
        });

        if (res?.error) {
            const message =
                res?.error?.message ??
                'Unable to update billing contact. Refresh the page and try again or contact support@ghost.org';

            captureBillingException(res?.error, {
                tags: {
                    source: 'billing-contact',
                    shown_to_user: true,
                },
                user: {
                    id: user.id,
                    email: user.email_address,
                    username: user?.stripe_token_sg,
                },
            });

            setError(message);
            setModalLoadingState(false);
            return setButtonState({ ...buttonStates.fail });
        }

        await getPartialBillingData('users');

        // SUCCESS!
        setModalLoadingState(false);
        setButtonState({ ...buttonStates.success });
        return onCloseModal({ reload: false, delay: 1000 });
    };

    const handleInputChange = (event) => {
        setBillingContact(event.target.value);
        setError(null);
        setButtonState({ ...buttonStates.default });
    };

    return (
        <form className="gh-form" onSubmit={handleSubmit}>
            <header className="billing-contact-header">
                <h1>Billing contact</h1>
                <p>
                    This email address will receive billing-related notifications, and can access
                    past invoices and receipts
                    <a
                        style={{ textDecoration: 'underline', marginLeft: '4px' }}
                        href="https://billing.stripe.com/p/login/aEU9COeOC1Ej51K5kk"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        here
                    </a>
                </p>
            </header>

            <div className="gh-input-group">
                <label htmlFor="billing-contact">Billing contact</label>
                <input
                    id="billing-contact"
                    className="gh-input"
                    name="name"
                    placeholder={user?.email_address || 'billing@example.com'}
                    autoCorrect="off"
                    data-testid="input-billing-contact"
                    autoFocus
                    value={billingContact}
                    onChange={(event) => handleInputChange(event)}
                    onBlur={(event) => sanitizeInput(event)}
                />
            </div>

            <SpinnerButton {...buttonState} data-test-btn="billing-contact" />

            {error && (
                <div className="form-error">
                    <p className="error-msg">{error}</p>
                </div>
            )}
        </form>
    );
}

BillingContact.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.string.isRequired,
        email_address: PropTypes.string.isRequired,
        billing_contact: PropTypes.string,
    }).isRequired,
    onCloseModal: PropTypes.func.isRequired,
    setModalLoadingState: PropTypes.func.isRequired,
};
